import React, { useState, useEffect } from 'react';
import '../../css/QnaManagement/Qna.css';
import Header from '../Header.js';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'http://13.124.224.246:7778';

const Qna = () => {
  const [qnaQuestions, setQnaQuestions] = useState([]);
  const [allQnaQuestions, setAllQnaQuestions] = useState([]);
  const [nicknameMap, setNicknameMap] = useState({}); // userId -> label(nickname/name/username/email) 캐시
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const navigate = useNavigate();

  /** userId를 문자열로 안전 추출 */
  const getUserIdString = (userId) => {
    if (!userId) return '';
    if (typeof userId === 'string') return userId;
    if (typeof userId === 'object') return userId._id || '';
    return '';
  };

  /** 질문 객체에서 표시용 작성자 라벨을 최대한 뽑아낸다 */
  const getBestAuthorLabel = (q, nMap = {}) => {
    const idStr = getUserIdString(q.userId);
    return (
      q.userId?.nickname ||
      q.authorNickname ||
      q.nickname ||
      q.userNickname ||
      q.username ||
      q.userId?.username ||
      q.email ||
      q.userEmail ||
      q.userId?.email ||
      nMap[idStr] ||        // 하이드레이션 결과(닉네임/이름/아이디/이메일 중 하나)
      idStr ||              // 마지막 수단: id라도 보여주기
      '알 수 없음'
    );
  };

  /** 이미 표시 가능한 이름이 들어있는지 */
  const qnaHasAnyName = (q) =>
    Boolean(
      q.userId?.nickname ||
      q.authorNickname ||
      q.nickname ||
      q.userNickname ||
      q.username ||
      q.userId?.username ||
      q.email ||
      q.userEmail ||
      q.userId?.email
    );

  /** populate가 없을 때 userId로 사용자 상세를 조회해 라벨을 채워 넣는다 */
  const hydrateNicknames = async (questions, token) => {
    const ids = Array.from(
      new Set(
        questions
          .filter((q) => !qnaHasAnyName(q))                  // 이미 이름정보 있으면 제외
          .map((q) => getUserIdString(q.userId))
          .filter(Boolean)
      )
    );
    if (!ids.length) return;

    try {
      const reqs = ids.map((id) =>
        axios
          .get(`${API_BASE}/api/users/userinfo/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => ({
            id,
            label:
              res?.data?.user?.nickname ||
              res?.data?.user?.name ||
              res?.data?.user?.username ||
              res?.data?.user?.email ||
              '',
          }))
          .catch((e) => {
            console.warn('닉네임 조회 실패:', id, e?.response?.status, e?.message);
            return { id, label: '' };
          })
      );

      const results = await Promise.all(reqs);
      const mapPatch = results.reduce((acc, { id, label }) => {
        if (id) acc[id] = label || '';
        return acc;
      }, {});

      if (Object.keys(mapPatch).length) {
        setNicknameMap((prev) => ({ ...prev, ...mapPatch }));
        // 현재 목록에도 바로 주입(표시 개선)
        setQnaQuestions((prev) =>
          prev.map((q) => {
            if (qnaHasAnyName(q)) return q;
            const id = getUserIdString(q.userId);
            const label = mapPatch[id];
            return label ? { ...q, authorNickname: label } : q;
          })
        );
        setAllQnaQuestions((prev) =>
          prev.map((q) => {
            if (qnaHasAnyName(q)) return q;
            const id = getUserIdString(q.userId);
            const label = mapPatch[id];
            return label ? { ...q, authorNickname: label } : q;
          })
        );
      }
    } catch (e) {
      console.warn('닉네임 하이드레이션 전체 실패:', e);
    }
  };

  useEffect(() => {
    const fetchQnaQuestions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(`${API_BASE}/api/qnaQuestion/getinfoAll`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response?.data?.success) {
          const questions = (response.data.questions || [])
            .slice()
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

          setQnaQuestions(questions);
          setAllQnaQuestions(questions);

          // populate가 없으면 사용자 라벨 하이드레이션 시도
          await hydrateNicknames(questions, token);

          // 구조 확인용(처음 3개만)
          console.log('QNA 샘플(최대 3):', questions.slice(0, 3));
        }
      } catch (error) {
        console.error('질문 데이터를 가져오는데 실패했습니다.', error);
      }
    };
    fetchQnaQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 검색 */
const handleSearch = () => {
   const term = searchTerm.trim().toLowerCase();

   // 사용자가 상태를 키워드로 명시한 경우 판별 (예: 완료/전/answered/unanswered/true/false 등)
   const statusTermToBool = (t) => {
     if (!t) return null;
     const pos = ['답변 완료','완료','답변완료','answered','answer','yes','true','1','o','y'];
     const neg = ['답변 전','미답변','미완료','답변전','unanswered','no','false','0','x','n'];
     if (pos.some(s => s.toLowerCase() === t)) return true;
     if (neg.some(s => s.toLowerCase() === t)) return false;
     return null; // 키워드가 모호하면 포함검색으로 처리
   };
   const statusQuery = statusTermToBool(term); // true/false/null

   const filtered = allQnaQuestions.filter((q) => {
     const title = String(q.title || '').toLowerCase();
     const author = String(getBestAuthorLabel(q, nicknameMap) || '').toLowerCase();
     const category = String(q.category || '').toLowerCase();

     const titleMatch = title.includes(term);
     const authorMatch = author.includes(term);
     const categoryMatch = category.includes(term);

     const isAnswered = Array.isArray(q.answers) && q.answers.length > 0;
     const statusKo = isAnswered ? '답변 완료' : '답변 전';
     const statusEn = isAnswered ? 'answered' : 'unanswered';
     const statusMatch =
       statusQuery !== null
         ? (isAnswered === statusQuery)
         : (statusKo.toLowerCase().includes(term) || statusEn.includes(term));

     if (searchCategory === 'all') {
       return titleMatch || authorMatch || categoryMatch || statusMatch;
     }
     if (searchCategory === 'title') return titleMatch;
     if (searchCategory === 'author') return authorMatch;
     if (searchCategory === 'category') return categoryMatch;
     if (searchCategory === 'status') return statusMatch;
     return true;
   });

   setQnaQuestions(filtered);
   setCurrentPage(1);
 };

  const handleQnaClick = (id) => {
    navigate(`/QnA/qna/qnaDetail/${id}`, { state: { id } });
  };

  // 페이지네이션
  const indexOfLastQuestion = currentPage * itemsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - itemsPerPage;
  const currentQuestions = qnaQuestions.slice(indexOfFirstQuestion, indexOfLastQuestion);
  const totalPages = Math.ceil(qnaQuestions.length / itemsPerPage) || 1;

  // 블록형 페이지네이션(10개씩)
  const pagesPerBlock = 10;
  const currentBlock = Math.floor((currentPage - 1) / pagesPerBlock);
  const startPage = currentBlock * pagesPerBlock + 1;
  const endPage = Math.min(startPage + pagesPerBlock - 1, totalPages);

  const handleBlockPrev = () => {
    if (startPage > 1) setCurrentPage(startPage - pagesPerBlock);
  };
  const handleBlockNext = () => {
    if (endPage < totalPages) setCurrentPage(endPage + 1);
  };

  return (
    <div className="qna-container">
      <Header />
      <div className="qna-content">
        <h1>1:1 문의</h1>

        <div className="qna-search-box">
          <div className="search-select-wrapper">
            <select
              className="search-category"
              value={searchCategory}
              onChange={(e) => setSearchCategory(e.target.value)}
            >
              <option value="all">전체</option>
              <option value="title">제목</option>
              <option value="author">작성자</option>
              <option value="category">카테고리</option>
              <option value="status">답변 상태</option>
            </select>
            <span className="select-icon">▼</span>
          </div>

          <input
            type="text"
            placeholder="검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="search-button" onClick={handleSearch}>
            검색
          </button>
        </div>

        <table className="qna-table">
          <thead>
            <tr>
              <th>번호</th>
              <th>제목</th>
              <th>카테고리</th>
              <th>작성자</th>
              <th>작성 날짜</th>
              <th>답변 상태</th>
            </tr>
          </thead>
          <tbody>
            {currentQuestions.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-results">
                  존재하지 않습니다.
                </td>
              </tr>
            ) : (
              currentQuestions.map((question, index) => {
                const isAnswered = Array.isArray(question.answers) && question.answers.length > 0;
                const authorLabel = getBestAuthorLabel(question, nicknameMap);
                return (
                  <tr key={question._id}>
                    <td>{allQnaQuestions.length - (indexOfFirstQuestion + index)}</td>
                    <td>
                      <span className="qna-title" onClick={() => handleQnaClick(question._id)}>
                        {question.title}
                      </span>
                    </td>
                    <td>{question.category || '-'}</td>
                    <td>{authorLabel}</td>
                    <td>{new Date(question.createdAt).toLocaleDateString()}</td>
                    <td className={isAnswered ? 'answered' : 'unanswered'}>
                      {isAnswered ? '답변 완료' : '답변 전'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div className="point-pagination">
          <button
            className="point-pagination-btn"
            onClick={handleBlockPrev}
            disabled={startPage === 1}
          >
            이전
          </button>
          {[...Array(endPage - startPage + 1)].map((_, idx) => {
            const pageNum = startPage + idx;
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`point-pagination-btn${currentPage === pageNum ? ' active' : ''}`}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            className="point-pagination-btn"
            onClick={handleBlockNext}
            disabled={endPage === totalPages}
          >
            다음
          </button>
        </div>
      </div>
    </div>
  );
};

export default Qna;
